import React, { ReactNode } from 'react';
import { Grid, useMediaQuery } from '@mui/material';
import { AccessTime, CalendarMonth, Groups, Timer } from '@mui/icons-material';
import { TFunction } from 'i18next';
import moment from 'moment';
import 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { OrderData } from '../../api';
import TimeUtils from '../../utils/TimeUtils';
import { phoneMediaQuery, rescheduleTheme } from '../rescheduleTheme';
import { RescheduleTypography } from './RescheduleTypography';

type InfoTilesProps = {
  date: string;
  time: string;
  participants: string;
  duration: string;
};

const formatClock = (secondsFromMidnight: number) => moment.unix(secondsFromMidnight).tz('utc').format('H:mm');

export const orderToInfoTiles = (order: OrderData, t: TFunction): InfoTilesProps => {
  const startSeconds = order.time ?? 0;
  return {
    date: moment(order.date).format('ddd D MMM'),
    time: `${formatClock(startSeconds)} – ${formatClock(startSeconds + order.duration)}`,
    participants: String(order.persons),
    duration: TimeUtils.formatDuration(order.duration, t).replace(/\s+/g, ' ').trim(),
  };
};

export const InfoTiles = ({ date, time, participants, duration }: InfoTilesProps) => {
  const { t } = useTranslation();
  const isPhone = useMediaQuery(phoneMediaQuery);

  const tiles: { key: string; label: string; value: string; icon: ReactNode }[] = [
    { key: 'date', label: t('manage_booking.v2.date'), value: date, icon: <CalendarMonth /> },
    { key: 'time', label: t('manage_booking.v2.time'), value: time, icon: <AccessTime /> },
    { key: 'participants', label: t('manage_booking.v2.participants'), value: participants, icon: <Groups /> },
    { key: 'duration', label: t('manage_booking.v2.duration'), value: duration, icon: <Timer /> },
  ];

  return (
    <Grid
      container
      sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: isPhone ? '8px' : '12px' }}>
      {tiles.map((tile) => (
        <Grid
          container
          key={tile.key}
          flexWrap="nowrap"
          alignItems="center"
          sx={{
            gap: '12px',
            padding: isPhone ? '12px' : '14px',
            background: rescheduleTheme.surfaceMuted,
            borderRadius: `${rescheduleTheme.radii.control}px`,
          }}>
          {!isPhone && (
            <Grid item aria-hidden="true" sx={{ display: 'flex', color: rescheduleTheme.brand, flex: 'none' }}>
              {tile.icon}
            </Grid>
          )}
          <Grid container flexDirection="column" flexWrap="nowrap" width="auto" minWidth={0} gap="2px">
            <RescheduleTypography style={{ fontSize: isPhone ? 12 : 13, color: rescheduleTheme.textMuted }}>
              {tile.label}
            </RescheduleTypography>
            <RescheduleTypography
              dir="ltr"
              style={{ fontSize: isPhone ? 15 : 16, fontWeight: 600, textAlign: 'start' }}>
              {tile.value}
            </RescheduleTypography>
          </Grid>
        </Grid>
      ))}
    </Grid>
  );
};
